// src/components/EnrolledCourse/LessonsPanel.jsx
import AccessTimeOutlinedIcon from "@mui/icons-material/AccessTimeOutlined";
import CalendarMonthOutlinedIcon from "@mui/icons-material/CalendarMonthOutlined";
import CheckCircleIcon from "@mui/icons-material/CheckCircle";
import ExpandLessIcon from "@mui/icons-material/ExpandLess";
import ExpandMoreIcon from "@mui/icons-material/ExpandMore";
import InsertDriveFileOutlinedIcon from "@mui/icons-material/InsertDriveFileOutlined";
import LinkOutlinedIcon from "@mui/icons-material/LinkOutlined";
import PlayCircleOutlineOutlinedIcon from "@mui/icons-material/PlayCircleOutlineOutlined";
import RadioButtonUncheckedIcon from "@mui/icons-material/RadioButtonUnchecked";
import {
  Box,
  Button,
  Chip,
  Divider,
  Grid,
  IconButton,
  Link as MuiLink,
  Paper,
  Stack,
  Typography,
} from "@mui/material";
import { useEffect, useState } from "react";
import { useTranslation } from "react-i18next";
import { downloadLessonContent } from "../../../redux/Actions/downloadLessonContent";


export default function LessonsPanel({ lessons = [] }) {
  const { t, i18n } = useTranslation(["ui"]);
  const isArabic = i18n.language === "ar";

  // track selected lesson + selected content
  const [selectedLessonId, setSelectedLessonId] = useState(null);
  const [selectedContentId, setSelectedContentId] = useState(null);
  const [expandedIds, setExpandedIds] = useState(new Set());

  // initialize selection to first lesson + first content
  useEffect(() => {
    if (!lessons.length) return;
    const firstLesson = lessons[0];
    setSelectedLessonId(firstLesson.id);
    setExpandedIds(new Set([firstLesson.id]));
    if (firstLesson.contents?.length) {
      setSelectedContentId(firstLesson.contents[0].id);
    } else {
      setSelectedContentId(null);
    }
  }, [lessons]);

  const selectedLesson = lessons.find((l) => l.id === selectedLessonId);
  const selectedContent =
    selectedLesson?.contents?.find((c) => c.id === selectedContentId) ||
    selectedLesson?.contents?.[0] ||
    null;

  const getCreated = (c) => c.createdDate || c.createdDat || null;

  const fmtDate = (d) =>
    d
      ? new Date(d).toLocaleDateString(isArabic ? "ar-EG" : "en-GB", {
          year: "numeric",
          month: "long",
          day: "numeric",
        })
      : "-";

  const toggleExpanded = (id) =>
    setExpandedIds((prev) => {
      const next = new Set(prev);
      next.has(id) ? next.delete(id) : next.add(id);
      return next;
    });

  // ---------------- empty state ----------------
  if (!lessons.length) {
    return (
      <Paper sx={{ p: 4, borderRadius: 2, textAlign: "center" }}>
        <Typography variant="h6" fontWeight={800} sx={{ mb: 1 }}>
          {t("courseDetails.lessons.noLessonsTitle")}
        </Typography>
        <Typography color="text.secondary">
          {t("courseDetails.lessons.noLessonsSub")}
        </Typography>
      </Paper>
    );
  }

  return (
    <Box sx={{ mt: 3 }}>
      <Grid container spacing={3}>
        {/* LEFT: lessons + contents (click selects content) */}
        <Grid item xs={12} md={12} lg={4}>
          <Paper sx={{ p: 2.5, borderRadius: 2 }}>
            <Typography
              variant="h6"
              fontWeight={800}
              sx={{ mb: 2, textAlign: isArabic ? "right" : "left" }}
            >
              {t("courseDetails.lessons.title")}
            </Typography>

            <Stack spacing={1.25}>
              {lessons.map((lesson) => {
                const expanded = expandedIds.has(lesson.id);
                const lessonContents = lesson.contents || [];

                return (
                  <Box
                    key={lesson.id}
                    sx={{
                      border: "1px solid #eee",
                      borderRadius: 2,
                      overflow: "hidden",
                      bgcolor:
                        selectedLessonId === lesson.id
                          ? "#f5f5f5ff"
                          : "#F6F8F8",
                    }}
                  >
                    {/* lesson header row */}
                    <Box
                      onClick={() => {
                        setSelectedLessonId(lesson.id);
                        // auto-pick first content for this lesson
                        if (lessonContents.length)
                          setSelectedContentId(lessonContents[0].id);
                        else setSelectedContentId(null);
                      }}
                      sx={{
                        display: "flex",
                        alignItems: "start",
                        gap: 1.25,
                        px: 1.5,
                        py: 1.25,
                        cursor: "pointer",
                      }}
                    >
                      <Chip
                        label={lesson.orderNumber ?? "-"}
                        size="small"
                        sx={{
                          my: 0.8,
                          fontWeight: 700,
                          borderRadius: "8px",
                          bgcolor: "#eff1f1ff",
                          color: "#617A79",
                        }}
                      />
                      <Box sx={{ flex: 1, minWidth: 0 }}>
                        <Typography
                          fontWeight={700}
                          sx={{
                            fontSize: 20,
                            whiteSpace: "nowrap",
                            overflow: "hidden",
                            textOverflow: "ellipsis",
                            textAlign: isArabic ? "right" : "left",
                          }}
                        >
                          {t("courseDetails.lessons.genericName")}{" "}
                          {lesson.orderNumber ?? ""}
                        </Typography>
                        <Typography
                          variant="caption"
                          color="text.secondary"
                          sx={{ display: "block", mt: 0.3 }}
                        >
                          <CalendarMonthOutlinedIcon
                            fontSize="inherit"
                            style={{
                              verticalAlign: "middle",
                              marginInlineEnd: 6,
                            }}
                          />
                          {fmtDate(lesson.date)}
                        </Typography>
                      </Box>
                      <IconButton
                        size="small"
                        onClick={(e) => {
                          e.stopPropagation();
                          toggleExpanded(lesson.id);
                        }}
                      >
                        {expanded ? <ExpandLessIcon /> : <ExpandMoreIcon />}
                      </IconButton>
                    </Box>

                    {/* collapsible content preview list */}
                    {expanded && (
                      <Box sx={{ px: 1.25, pb: 1.25 }}>
                        {lesson.zoomLink && (
                          <Button
                            size="small"
                            href={lesson.zoomLink}
                            target="_blank"
                            sx={{ fontWeight: 700, mb: 2 }}
                          >
                            {t("courseDetails.lessons.joinNow")} (Zoom)
                          </Button>
                        )}
                        {lessonContents.length ? (
                          <Stack spacing={0.5}>
                            {lessonContents.map((c) => {
                              const isSelected =
                                selectedLessonId === lesson.id &&
                                selectedContentId === c.id;
                              return (
                                <Box
                                  key={c.id}
                                  onClick={() => {
                                    setSelectedLessonId(lesson.id);
                                    setSelectedContentId(c.id);
                                  }}
                                  sx={{
                                    display: "flex",
                                    alignItems: "center",
                                    gap: 1,
                                    px: 1,
                                    py: 0.75,
                                    width: "fit-content",
                                    borderRadius: 1,
                                    cursor: "pointer",
                                    "&:hover": { bgcolor: "#e9ececff" },
                                  }}
                                >
                                  {isSelected ? (
                                    <CheckCircleIcon
                                      fontSize="small"
                                      color="success"
                                    />
                                  ) : (
                                    <RadioButtonUncheckedIcon fontSize="small" />
                                  )}
                                  <Typography
                                    variant="body2"
                                    sx={{
                                      fontWeight: isSelected ? 700 : 500,
                                      textAlign: isArabic ? "right" : "left",
                                    }}
                                  >
                                    {t("courseDetails.lessons.content")}{" "}
                                    {c.orderNumber ?? ""}
                                  </Typography>
                                </Box>
                              );
                            })}
                          </Stack>
                        ) : (
                          <Typography
                            variant="body2"
                            color="text.secondary"
                            sx={{ py: 0.6 }}
                          >
                            {t("courseDetails.lessons.noContentShort")}
                          </Typography>
                        )}
                      </Box>
                    )}
                  </Box>
                );
              })}
            </Stack>
          </Paper>
        </Grid>

        {/* RIGHT: selected lesson + selected content details */}
        <Grid item xs={12} sm={12} md={12} lg={8}>
          <Paper sx={{ p: 2.5, borderRadius: 2 }}>
            <Typography
              variant="h6"
              fontWeight={800}
              sx={{ mb: 2, textAlign: isArabic ? "right" : "left" }}
            >
              {t("courseDetails.lessons.contentTitle")}
            </Typography>

            <Divider sx={{ mb: 2 }} />

            {/* message row */}
            <Box
              sx={{
                mb: 1.5,
                p: 1.25,
                bgcolor: "#F6F8F8",
                borderRadius: 1.5,
              }}
            >
              <Typography
                variant="caption"
                color="text.secondary"
                sx={{ mb: 0.5, display: "block" }}
              >
                {t("courseDetails.lessons.message")}
              </Typography>
              <Typography>
                {selectedContent?.message ||
                  t("courseDetails.lessons.noMessage")}
              </Typography>
            </Box>

            {/* files row */}
            <Box
              sx={{
                mb: 1.5,
                p: 1.25,
                bgcolor: "#F6F8F8",
                borderRadius: 1.5,
              }}
            >
              <Typography
                variant="caption"
                color="text.secondary"
                sx={{ mb: 0.5, display: "block" }}
              >
                {t("courseDetails.lessons.attachments")}
              </Typography>

              {selectedContent?.file ? (
                <Stack direction="row" alignItems="center" spacing={1}>
                  <InsertDriveFileOutlinedIcon
                    fontSize="small"
                    sx={{
                      marginLeft: isArabic ? "8px" : 0,
                      marginRight: isArabic ? 0 : "8px",
                    }}
                  />
                  {/* download link needs fix */}
                     <MuiLink
                    component="button"
                    onClick={downloadLessonContent(selectedContent.filePath)}   // correct
                    underline="hover"
                    sx={{ cursor: "pointer" }}
                  >
                    {selectedContent.file.split("_").slice(1).join("_") || selectedContent.file}
                  </MuiLink>
                  {getCreated(selectedContent) && (
                    <Typography
                      variant="caption"
                      color="text.secondary"
                      sx={{ ml: "auto" }}
                    >
                      <AccessTimeOutlinedIcon
                        fontSize="inherit"
                        style={{ verticalAlign: "middle", marginInlineEnd: 4 }}
                      />
                      {t("courseDetails.lessons.created")}:{" "}
                      {fmtDate(getCreated(selectedContent))}
                    </Typography>
                  )}
                </Stack>
              ) : (
                <Typography variant="body2" color="text.secondary">
                  {t("courseDetails.lessons.noFiles")}
                </Typography>
              )}
            </Box>

            {/* links row */}
            <Box
              sx={{
                mb: 0.5,
                p: 1.25,
                bgcolor: "#F6F8F8",
                borderRadius: 1.5,
              }}
            >
              <Typography
                variant="caption"
                color="text.secondary"
                sx={{ mb: 0.5, display: "block" }}
              >
                {t("courseDetails.lessons.links")}
              </Typography>

              {selectedContent?.link ? (
                <Stack direction="row" alignItems="center" spacing={1}>
                  {/youtube\.com|youtu\.be/.test(selectedContent.link) ? (
                    <PlayCircleOutlineOutlinedIcon
                      fontSize="small"
                      sx={{
                        marginLeft: isArabic ? "8px" : 0,
                        marginRight: isArabic ? 0 : "8px",
                      }}
                    />
                  ) : (
                    <LinkOutlinedIcon
                      fontSize="small"
                      sx={{
                        marginLeft: isArabic ? "8px" : 0,
                        marginRight: isArabic ? 0 : "8px",
                      }}
                    />
                  )}
                  <MuiLink
                    href={selectedContent.link}
                    target="_blank"
                    rel="noopener noreferrer"
                    underline="hover"
                  >
                    {/youtube\.com|youtu\.be/.test(selectedContent.link)
                      ? t("courseDetails.lessons.videoLink")
                      : t("courseDetails.lessons.externalLink")}
                  </MuiLink>

                  {selectedContent.deadline && (
                    <Typography
                      variant="caption"
                      color="error"
                      sx={{ ml: "auto" }}
                    >
                      {t("courseDetails.lessons.deadline")}:{" "}
                      {fmtDate(selectedContent.deadline)}
                    </Typography>
                  )}
                </Stack>
              ) : (
                <Typography variant="body2" color="text.secondary">
                  {t("courseDetails.lessons.noLinks")}
                </Typography>
              )}
            </Box>
          </Paper>
        </Grid>
      </Grid>
    </Box>
  );
}
